// hooks.js
import { useCallback, useContext, useMemo } from "react";
import { REMOVE_PERSON_FROM_INCIDENT } from "../../Mutations/RemovePersonFromIncident";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { UserContext } from "../../Contexts/UserContext";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import {
  isPisEmpty,
  personHasSwAssociation,
  personHasTicketsOrArrests,
} from "./utils";
import { handleUuid } from "../../utils/utils";
import { ADD_OR_UPDATE_PERSON_IN_INCIDENT } from "../../Mutations/AddOrUpdatePersonInIncident";
import { throttlePromiseFunction } from "../../utils/throttlePromiseFunction";
import { useApolloClient } from "@apollo/client";

export const useHandleRemovePerson = incident => {
  const { REMOVE_PIS } = INCIDENT_ACTIONS;
  const { onlineState, user } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const client = useApolloClient();

  const handleRemovePerson = useCallback(
    async pis => {
      return new Promise((resolve, reject) => {
        (async () => {
          let errors = [];
          const person = pis.person;
          if (!pis.id) {
            errors.push("PIS not found");
            resolve({ success: false, errors });
            return;
          }
          if (!incident.id) {
            errors.push("Incident not found");
            resolve({ success: false, errors });
            return;
          }
          if (!person) {
            alert("Person not found");
            errors.push("Person not found");
            resolve({ success: false, errors });
            return;
          }
          if (window.confirm(`Are you sure you want to remove this person?`)) {
            if (onlineState) {
              const { hasSwAssociation, swAssociationErrors } =
                await personHasSwAssociation(person, incident, user, client);
              if (hasSwAssociation) {
                errors = errors.concat(swAssociationErrors);
                resolve({ success: false, errors });
                return;
              }
              try {
                client
                  .mutate({
                    mutation: REMOVE_PERSON_FROM_INCIDENT,
                    variables: {
                      input: {
                        personIncidentSpecificId: pis.id,
                        originatorId: user?.id,
                      },
                    },
                  })
                  .then(mutationRes => {
                    if (
                      mutationRes?.data?.removePersonFromIncident?.errors
                        ?.length > 0
                    ) {
                      errors = errors.concat(
                        mutationRes?.data?.removePersonFromIncident?.errors,
                      );
                      resolve({ success: false, errors });
                      return;
                    } else {
                      try {
                        incidentDispatch({
                          type: REMOVE_PIS,
                          payload: {
                            incidentId: incident?.id
                              ? incident.id
                              : incident.uuid,
                            pisId: pis.id,
                          },
                        });
                        resolve({ success: true, errors });
                        return;
                      } catch (err) {
                        errors.push(err);
                        errors.push(
                          "Could not update your local state. Please refresh the page.",
                        );
                        reject({ success: false, errors });
                        return;
                      }
                    }
                  })
                  .catch(reject);
              } catch (err) {
                errors.push(err);
                reject({ success: false, errors });
                return;
              }
            } else {
              //offline path with state check first
              const { hasAssociation, associationErrors } =
                await personHasTicketsOrArrests(person, incident, user, client);
              if (hasAssociation) {
                errors = errors.concat(associationErrors);
                resolve({ success: false, errors });
                return;
              } else {
                try {
                  incidentDispatch({
                    type: REMOVE_PIS,
                    payload: {
                      incidentId: incident?.id ? incident.id : incident.uuid,
                      pisId: pis.id,
                    },
                  });
                  client.mutate({
                    mutation: REMOVE_PERSON_FROM_INCIDENT,
                    variables: {
                      input: {
                        personIncidentSpecificId: pis.id,
                        originatorId: user?.id,
                      },
                    },
                  }).catch(error => {
                    console.error("Offline person removal failed", error);
                  });
                  resolve({ success: true, errors });
                  return;
                } catch (err) {
                  errors.push(err);
                  reject({ success: false, errors });
                  return;
                }
              }
            }
          } else {
            resolve({ success: true, cancelled: true, errors });
          }
        })().catch(reject);
      });
    },
    [incident, onlineState, user, incidentDispatch, REMOVE_PIS, client],
  );
  return useMemo(
    () => throttlePromiseFunction(handleRemovePerson),
    [handleRemovePerson],
  );
};

export const useHandleAddPersonIncidentSpecific = (incident, callback) => {
  const { ADD_PIS } = INCIDENT_ACTIONS;
  const { onlineState, user } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const client = useApolloClient();

  const handleAddPISToIncident = useCallback(async () => {
    return new Promise((resolve, reject) => {
      (async () => {
        let errors = [];
        const piss = incident.personIncidentSpecifics;
        const lastPis = piss?.[piss.length - 1] || null;
        const pisIsEmpty = await isPisEmpty(lastPis, incident, user, client);

        if (!incident.id) {
          errors.push("Incident not found");
          resolve({ success: false, errors });
          return;
        }

        if (pisIsEmpty) {
          errors.push(
            "Please add a person to this entry before adding a new blank entry",
          );
          resolve({ success: false, errors });
          return;
        }

        const pisUuid = handleUuid();
        const personUuid = handleUuid();
        const personalAttributeUuid = handleUuid();

        const incidentInvolvement = {
          id: "1",
          name: "Caller",
          oriId: "1",
          position: 1,
        };

        let pis = {
          id: pisUuid,
          uuid: pisUuid,
          person: {
            id: personUuid,
            uuid: personUuid,
            personalAttribute: {
              id: personalAttributeUuid,
              uuid: personalAttributeUuid,
            },
          },
          incidentInvolvement: incidentInvolvement,
        };

        //online path
        //The last pis has a person already so add a new pis with a new person
        if (onlineState) {
          try {
            client
              .mutate({
                mutation: ADD_OR_UPDATE_PERSON_IN_INCIDENT,
                variables: {
                  input: {
                    originatorId: user?.id,
                    incidentId: incident?.id ? incident.id : incident?.uuid,
                    personIncidentSpecificId: pis.id,
                    personId: pis.person.id,
                  },
                },
              })
              .then(mutationRes => {
                const returnedPis =
                  mutationRes?.data?.addOrUpdatePersonInIncident
                    ?.personIncidentSpecific;
                if (
                  mutationRes?.data?.addOrUpdatePersonInIncident?.errors
                    ?.length > 0
                ) {
                  errors = errors.concat(
                    mutationRes?.data?.addOrUpdatePersonInIncident?.errors,
                  );
                  resolve({ success: false, errors });
                  return;
                } else {
                  incidentDispatch({
                    type: ADD_PIS,
                    payload: { incidentId: incident.id, pis: returnedPis },
                  });
                  !!callback && callback();
                  resolve({ success: true, errors });
                  return;
                }
              })
              .catch(reject);
          } catch (err) {
            errors.push(err);
            reject({ success: false, errors });
            return;
          }
        } else {
          //offline path
          try {
            incidentDispatch({
              type: ADD_PIS,
              payload: { incidentId: incident.id, pis: pis },
            });
            !!callback && callback();
            client.mutate({
              mutation: ADD_OR_UPDATE_PERSON_IN_INCIDENT,
              variables: {
                input: {
                  originatorId: user?.id,
                  incidentId: incident?.id ? incident.id : incident?.uuid,
                  personIncidentSpecificId: pis.id,
                  personId: pis.person.id,
                },
              },
            }).catch(error => {
              console.error("Offline person addition failed", error);
            });
            resolve({ success: true, errors });
            return;
          } catch (err) {
            errors.push(err);
            reject({ success: false, errors });
            return;
          }
        }
      })().catch(reject);
    });
  }, [
    ADD_PIS,
    callback,
    incident,
    incidentDispatch,
    onlineState,
    user,
    client,
  ]);

  return useMemo(
    () => throttlePromiseFunction(handleAddPISToIncident),
    [handleAddPISToIncident],
  );
};
